import React, { FC, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import Mousetrap from 'mousetrap';
import isEmpty from 'lodash/isEmpty';
import { VaultButton } from '../../../Shared';
import {
  MarkTime,
  handleMarkNewClip,
  selectIsMarkOutDisabled,
} from '../../state/clipList';
import { selectSource } from '../../state/clipperMediaContent';
import {
  ALT_MARK_IN, ALT_MARK_OUT, MARK_IN, MARK_OUT,
} from '../../constants/hotKeys';
import { IAppState } from '../../../../state/reducers';
import { selectCurrentTime } from '../../state/videoPlayer';
import { IVideoClip } from '../../../../api/vault/clipList';

const Container = styled.div`
  height: 60px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-direction: row;
`;

interface IStateProps {
  isPlayerReady: boolean;
  sourceUrl: string;
  isMarkOutDisabled: boolean;
  isKeyboardControlsEnabled: boolean;
  currentEditingClipIndex: number;
  currentTime: number;
  clips: IVideoClip[],
}

interface IDispatchProps {
  handleMarkNewClip: (time: MarkTime) => void;
}

interface IProps extends IStateProps, IDispatchProps { }

const ButtonArea: FC<IProps> = (props: IProps) => {
  const {
    isKeyboardControlsEnabled,
    isMarkOutDisabled,
    sourceUrl,
    currentEditingClipIndex,
  } = props;

  useEffect(() => {
    updateMarkBindings();
    return () => {
      unbindHotKeys();
    };
  }, [isKeyboardControlsEnabled, isMarkOutDisabled, sourceUrl, currentEditingClipIndex]);

  const unbindHotKeys = () => {
    Mousetrap.unbind(MARK_IN);
    Mousetrap.unbind(MARK_OUT);
    Mousetrap.unbind(ALT_MARK_IN);
    Mousetrap.unbind(ALT_MARK_OUT);
  };

  const updateMarkBindings = () => {
    const {
      isPlayerReady,
      sourceUrl,
      isMarkOutDisabled,
      isKeyboardControlsEnabled,
    } = props;

    if (isKeyboardControlsEnabled) {
      if (isPlayerReady && !isEmpty(sourceUrl)) {
        Mousetrap.bind(MARK_IN, () => { handleMarkClick(MarkTime.In); });
        Mousetrap.bind(ALT_MARK_IN, () => { handleMarkClick(MarkTime.In); });
        if (!isMarkOutDisabled) {
          Mousetrap.bind(MARK_OUT, () => { handleMarkClick(MarkTime.Out); });
          Mousetrap.bind(ALT_MARK_OUT, () => { handleMarkClick(MarkTime.Out); });
        } else {
          Mousetrap.unbind(MARK_OUT);
          Mousetrap.unbind(ALT_MARK_OUT);
        }
      }
    } else {
      unbindHotKeys();
    }
  };

  const isMarkDisabled = (time: MarkTime) => {
    const {
      currentEditingClipIndex,
      currentTime,
      clips,
      isMarkOutDisabled,
    } = props;
    if (currentEditingClipIndex !== -1) {
      const { markInTime, markOutTime } = clips[currentEditingClipIndex];
      if (time === MarkTime.In) return markOutTime !== null && currentTime >= markOutTime;
      return markInTime === null || currentTime <= markInTime;
    }
    if (time === MarkTime.Out) return isMarkOutDisabled;
    return false;
  };

  const handleMarkClick = (time: MarkTime) => {
    const { handleMarkNewClip } = props;

    handleMarkNewClip(time);
  };

  return (
    <Container id="ButtonArea">
      <VaultButton
        id="MarkIn2"
        variant="plainOnPaper"
        disabled={isMarkDisabled(MarkTime.In)}
        onClick={() => { handleMarkClick(MarkTime.In); }}
      >
        Mark In
      </VaultButton>
      {' '}
      <VaultButton
        id="MarkOut2"
        disabled={isMarkDisabled(MarkTime.Out)}
        variant="plainOnPaper"
        onClick={() => { handleMarkClick(MarkTime.Out); }}
      >
        Mark Out
      </VaultButton>
    </Container>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  isPlayerReady: state.videoPlayer.isReady,
  sourceUrl: selectSource(state),
  isMarkOutDisabled: selectIsMarkOutDisabled(state),
  isKeyboardControlsEnabled: state.videoPlayer.isKeyboardControlsEnabled,
  currentEditingClipIndex: state.currentEditingClip.currentIndex,
  currentTime: selectCurrentTime(state),
  clips: state.clipList.allClips,
});

const mapDispatchToProps: IDispatchProps = { handleMarkNewClip };

export default connect(mapStateToProps, mapDispatchToProps)(ButtonArea);
